import { Dispatch, SetStateAction } from 'react';
import {
  Checkbox,
  Info,
  Input,
  PrimaryButton,
  SecondaryButton,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import { useForm } from 'react-hook-form';
import { ButtonContainer } from '../../ButtonContainer/ButtonContainer';
import { IGlobalItem, Steps } from '../types';
import { FormVariant } from 'src/types/menuItemForm.types';
import styles from './GlobalItemTaxRateForm.module.scss';
import WarningBanner from 'src/components/WarningBanner/WarningBanner';
import { GLOBAL_ITEM_UPDATE_NOTE } from 'src/constants';
import { useAuthWrapper } from '@weareopenr/authkit';

export type TGlobalItemTaxRateData = {
  deposit_return_scheme_checkbox: boolean;
  deposit_return_scheme: string;
  tax_rate: string;
};
const taxRateErrorMessage = 'Please enter a valid tax rate between 0 - 100 (%)';

interface IGlobalItemTaxRateForm {
  variant: FormVariant;
  item: Partial<IGlobalItem>;
  isSubmitting: boolean;
  onSubmitItem: (data: Partial<IGlobalItem>, isDirty: boolean) => void | undefined;
  onCloseOrBack: (nextStep?: Steps) => void;
  setGlobalItem?: Dispatch<SetStateAction<Partial<IGlobalItem>>>;
}

export const GlobalItemTaxRateForm = ({
  variant,
  item,
  isSubmitting,
  onSubmitItem,
  onCloseOrBack,
  setGlobalItem,
}: IGlobalItemTaxRateForm) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<TGlobalItemTaxRateData>({
    defaultValues: {
      tax_rate: item.tax_rate || '',
      deposit_return_scheme:
        item.deposit_return_scheme === null || item.deposit_return_scheme === undefined
          ? ''
          : item.deposit_return_scheme.toString(),
      deposit_return_scheme_checkbox: item.deposit_return_scheme !== null && item.deposit_return_scheme !== undefined,
    },
    mode: 'onChange',
  });

  const { featureFlags } = useAuthWrapper();
  const showDepositReturnScheme = featureFlags.includes('deposit_return_scheme');
  const titleMap = new Map<FormVariant, string>([
    [FormVariant.CREATE, `Add Tax Rate${showDepositReturnScheme ? ' & Deposit Fee' : ''}`],
    [FormVariant.UPDATE, `Edit Tax${showDepositReturnScheme ? ' & Deposit Fee' : ''}`],
  ]);

  const isDepositChecked = watch('deposit_return_scheme_checkbox');

  const onSubmit = ({ deposit_return_scheme_checkbox, deposit_return_scheme, tax_rate }: TGlobalItemTaxRateData) => {
    const drs = {
      deposit_return_scheme: deposit_return_scheme_checkbox ? deposit_return_scheme : null,
    };
    if (onSubmitItem) {
      onSubmitItem({ ...item, ...drs, tax_rate }, isDirty);
    } else {
      setGlobalItem({ ...drs, tax_rate });
      onCloseOrBack(Steps.bundleCategory);
    }
  };

  return (
    <div className={styles['main-container']}>
      <div className={styles['title-container']}>
        <Typography weight="semi-bold" size="xxlarge">
          {titleMap.get(variant)}
        </Typography>
        {variant === FormVariant.UPDATE && <WarningBanner text={GLOBAL_ITEM_UPDATE_NOTE} />}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} aria-label="taxrate-info-form">
        <Input
          type="text"
          label="Tax rate (%)"
          hasMargin
          register={register('tax_rate', {
            required: taxRateErrorMessage,
            pattern: { value: /^[%.0-9]+$/i, message: taxRateErrorMessage },
            max: { value: 100, message: taxRateErrorMessage },
            min: { value: 0, message: taxRateErrorMessage },
          })}
          error={errors?.tax_rate?.message}
        />
        {showDepositReturnScheme && (
          <>
            <div className={styles['typography-spacing']}>
              <Typography weight="semi-bold">Deposit Fee</Typography>
              <Tooltip text="This is the price in cents for the levy charge applied against goods in Ireland as part of the Deposit Return Scheme.">
                <Info />
              </Tooltip>
            </div>
            <Checkbox
              name="deposit_return_scheme_checkbox"
              register={register}
              label="Deposit fee Required"
              labelWeight="regular"
            />
            {isDepositChecked && (
              <Input
                type="text"
                label="Deposit Fee"
                hasMargin
                data-test-id="deposit_return_scheme"
                register={register('deposit_return_scheme', {
                  required: 'This field must contain a value for the deposit fee. Please correct.',
                  pattern: {
                    value: /^\d+(\.\d+)?$/i,
                    message: 'Please enter a valid deposit fee.',
                  },
                })}
                error={errors?.deposit_return_scheme?.message}
              />
            )}
          </>
        )}
        <ButtonContainer
          left={
            <SecondaryButton
              size="full-width"
              buttonStyle="outline"
              type="button"
              onClick={() => (variant === FormVariant.CREATE ? onCloseOrBack(Steps.details) : onCloseOrBack())}
            >
              {variant === FormVariant.CREATE ? 'Back' : 'Cancel'}
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting}>
              {variant === FormVariant.CREATE && !onSubmitItem ? 'Continue' : 'Save'}
            </PrimaryButton>
          }
        />
      </form>
    </div>
  );
};
